import type { VectorGeometryResult, WeldScoreConfig } from '../../types';

export interface SvgExportOptions {
  geometry: VectorGeometryResult;
  weldScoreConfig: WeldScoreConfig;
  marginMm?: number;
  includeCut?: boolean;
  includeScore?: boolean;
  includeBacker?: boolean;
  includeGuides?: boolean;
}

/**
 * Generates an ultra-clean, industry-standard laser cutting SVG with color-separated layers.
 */
export function generateLaserSvg(options: SvgExportOptions): string {
  const {
    geometry,
    weldScoreConfig,
    marginMm = 5,
    includeCut = true,
    includeScore = true,
    includeBacker = true,
    includeGuides = true,
  } = options;

  const { minX, minY, widthMm, heightMm } = geometry.bounds;
  const vbX = minX - marginMm;
  const vbY = minY - marginMm;
  const vbW = widthMm + marginMm * 2;
  const vbH = heightMm + marginMm * 2;

  let layersXml = '';

  // 1. Backer Plate Layer (Green / Separate Cut Layer)
  if (includeBacker && geometry.backerPaths.length > 0) {
    layersXml += `  <!-- LAYER: BACKER CUT (Outer Base Plate) -->\n`;
    layersXml += `  <g id="backer-cut" stroke="#00CC00" stroke-width="0.1" fill="none" stroke-linecap="round" stroke-linejoin="round">\n`;
    for (const d of geometry.backerPaths) {
      layersXml += `    <path d="${d}" />\n`;
    }
    layersXml += `  </g>\n`;
  }

  // 2. Alignment Guide Layer (Cyan / Scored onto Backer)
  if (includeGuides && geometry.guidePaths.length > 0) {
    layersXml += `  <!-- LAYER: ALIGNMENT GUIDES (Score onto backer) -->\n`;
    layersXml += `  <g id="alignment-guides" stroke="#00CCCC" stroke-width="0.1" stroke-dasharray="1,1" fill="none">\n`;
    for (const d of geometry.guidePaths) {
      layersXml += `    <path d="${d}" />\n`;
    }
    layersXml += `  </g>\n`;
  }

  // 3. Score Layer (Blue / Internal Letter Overlaps)
  if (includeScore && geometry.scorePaths.length > 0) {
    layersXml += `  <!-- LAYER: SCORE (Surface Etch / Letter Overlaps) -->\n`;
    layersXml += `  <g id="score" stroke="${weldScoreConfig.scoreColor || '#0055FF'}" stroke-width="0.1" fill="none" stroke-linecap="round" stroke-linejoin="round">\n`;
    for (const d of geometry.scorePaths) {
      layersXml += `    <path d="${d}" />\n`;
    }
    layersXml += `  </g>\n`;
  }

  // 4. Main Cut Layer (Red / Welded Text Boundary)
  if (includeCut && geometry.cutPaths.length > 0) {
    layersXml += `  <!-- LAYER: CUT (Main Outer Outline) -->\n`;
    layersXml += `  <g id="cut" stroke="${weldScoreConfig.cutColor || '#FF0000'}" stroke-width="0.1" fill="none" stroke-linecap="round" stroke-linejoin="round">\n`;
    for (const d of geometry.cutPaths) {
      layersXml += `    <path d="${d}" />\n`;
    }
    layersXml += `  </g>\n`;
  }

  return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg 
  xmlns="http://www.w3.org/2000/svg" 
  width="${round(vbW)}mm" 
  height="${round(vbH)}mm" 
  viewBox="${round(vbX)} ${round(vbY)} ${round(vbW)} ${round(vbH)}"
  version="1.1"
  data-generator="Disit Laser Studio (Cuttle-Compatible)">
  <title>Laser Cut Design</title>
  <desc>Color convention: Red (#FF0000) = Cut, Blue (#0055FF) = Score, Green (#00CC00) = Backer Cut, Cyan (#00CCCC) = Alignment Guides</desc>
${layersXml}</svg>`;
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
